import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Beaker, ShieldCheck, Truck, Microscope, Cpu, ArrowRight } from "lucide-react";

/* Six rotating research signatures — displayed one at a time on the right of
 * the mascot lockup. Auto-advances every 4s (respects reduced-motion). */
const CALLOUTS = [
    {
        icon: Microscope,
        eyebrow: "// Assay",
        title: "HPLC verified purity",
        body: "Every batch ships with the manufacturer-supplied Certificate of Analysis — high-performance liquid chromatography plus mass spectrometry, referenced back to the vial lot.",
    },
    {
        icon: ShieldCheck,
        eyebrow: "// Handling",
        title: "Cold-chain custody",
        body: "Peptides are held at 2–8°C from freezer to your bench with tamper-evident seals. Cold packs and insulated packaging on every domestic dispatch.",
    },
    {
        icon: Truck,
        eyebrow: "// Logistics",
        title: "24-hour Sydney dispatch",
        body: "Orders placed before 3pm AEST ship the same business day from our Sydney facility. Tracked, discreet, and signature-optional.",
    },
    {
        icon: Beaker,
        eyebrow: "// Reconstitution",
        title: "Lab-grade solvents in stock",
        body: "Bacteriostatic water, sterile syringes, insulin syringes and alcohol swabs available alongside every research compound — a single dispatch, no third-party splits.",
    },
    {
        icon: Cpu,
        eyebrow: "// Traceability",
        title: "Lot-level provenance",
        body: "Every vial's lot number links to its COA record in our library. Enter it at check-in and see the exact assay data the manufacturer supplied.",
    },
    {
        icon: ShieldCheck,
        eyebrow: "// Compliance",
        title: "Research use only",
        body: "All compounds are sold strictly for in-vitro and in-vivo laboratory research. Not for human consumption, diagnostic, or therapeutic use.",
    },
];

export const MascotShowcase = () => {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        const reduce = typeof window !== "undefined"
            && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce || paused) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % CALLOUTS.length), 4200);
        return () => clearInterval(t);
    }, [paused]);

    const active = CALLOUTS[idx];
    const Icon = active.icon;

    return (
        <section
            className="relative py-20 border-t border-white/5 overflow-hidden"
            data-testid="mascot-showcase"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Deep cyan grid backdrop */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.06]"
                style={{
                    backgroundImage: `
                        linear-gradient(0deg, rgba(0,229,255,0.6) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,229,255,0.6) 1px, transparent 1px)
                    `,
                    backgroundSize: "48px 48px",
                    maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
                }}
                aria-hidden="true"
            />

            <div className="container-x relative">
                <div className="flex items-center gap-3 mb-10 sm:mb-14">
                    <span className="cyan-bar" />
                    <span className="tech-label">// Signature</span>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                    {/* Mascot lockup */}
                    <div className="lg:col-span-5 relative">
                        <div className="relative cfibre-panel-cyan rounded-sm aspect-square max-w-md mx-auto p-6 overflow-hidden">
                            {/* Corner ticks */}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[var(--cyan)]" />
                            <span aria-hidden="true" className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[var(--cyan)]" />
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-[var(--cyan)]" />
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-[var(--cyan)]" />
                            {/* Radial cyan pool */}
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background: "radial-gradient(circle at 50% 55%, rgba(0,229,255,0.28) 0%, transparent 55%)",
                                }}
                                aria-hidden="true"
                            />
                            <img
                                src="/brand/mascot.png"
                                alt="Carbon Labs mascot"
                                className="relative z-10 w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,229,255,0.35)]"
                                loading="lazy"
                                data-testid="mascot-showcase-img"
                            />
                            {/* Serial ID */}
                            <div className="absolute bottom-3 left-3 z-10 text-[9px] font-mono tracking-[0.3em] text-[var(--cyan)]/70 uppercase">
                                CL-M01 · Ø
                            </div>
                            <div className="absolute bottom-3 right-3 z-10 text-[9px] font-mono tracking-[0.3em] text-white/40 uppercase">
                                v2.6
                            </div>
                        </div>
                    </div>

                    {/* Rotating callout */}
                    <div className="lg:col-span-7">
                        <div key={idx} className="fade-up">
                            <div className="inline-flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-sm border border-[rgba(0,229,255,0.35)] bg-[rgba(0,229,255,0.06)] flex items-center justify-center">
                                    <Icon size={18} className="text-[var(--cyan)]" />
                                </div>
                                <span className="tech-label">{active.eyebrow}</span>
                            </div>
                            <h2
                                className="font-display text-3xl md:text-5xl font-black text-white tracking-tighter leading-[1.05] mb-5"
                                data-testid="mascot-callout-title"
                            >
                                {active.title}
                            </h2>
                            <p className="text-[var(--text-secondary)] text-base md:text-lg max-w-xl leading-relaxed">
                                {active.body}
                            </p>

                            {/* Cyan hairline */}
                            <div className="cyan-line my-8 max-w-md" />

                            <div className="flex flex-wrap items-center gap-6">
                                <Link
                                    to="/shop"
                                    className="btn-solid inline-flex"
                                    data-testid="mascot-cta-shop"
                                >
                                    Browse Catalogue <ArrowRight size={14} />
                                </Link>
                                <Link
                                    to="/lab-standards"
                                    className="text-xs font-mono uppercase tracking-[0.28em] text-white/70 hover:text-[var(--cyan)] transition-colors"
                                    data-testid="mascot-cta-standards"
                                >
                                    Read the Lab Standards →
                                </Link>
                            </div>
                        </div>

                        {/* Indicator rail */}
                        <div
                            className="mt-10 flex items-center gap-2"
                            role="tablist"
                            aria-label="Signature callouts"
                        >
                            {CALLOUTS.map((_, i) => (
                                <button
                                    key={i}
                                    role="tab"
                                    aria-selected={i === idx}
                                    aria-label={`Show callout ${i + 1}`}
                                    onClick={() => setIdx(i)}
                                    data-testid={`mascot-indicator-${i}`}
                                    className={`transition-all h-1 rounded-full ${
                                        i === idx
                                            ? "w-10 bg-[var(--cyan)] shadow-[0_0_10px_rgba(0,229,255,0.7)]"
                                            : "w-4 bg-white/15 hover:bg-white/30"
                                    }`}
                                />
                            ))}
                            <span className="ml-3 text-[10px] font-mono tracking-[0.28em] uppercase text-white/40">
                                {String(idx + 1).padStart(2, "0")} / {String(CALLOUTS.length).padStart(2, "0")}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
